import { motion } from "framer-motion";
import { FadeUp, MaskedLineReveal } from "./motion/Reveal";
import { TEST_IDS } from "../constants/testIds";
import { ArrowUpRight, Droplets, Flame, Wind } from "lucide-react";

const PILLARS = [
  {
    slug: "idrosanitari",
    code: "01",
    name: "Impianti Idrosanitari",
    tag: "Distribuzione ACS/AFS",
    icon: Droplets,
    desc:
      "Distribuzione idrica sanitaria, produzione acqua calda e integrazioni con solare termico.",
    img: "https://images.pexels.com/photos/7937299/pexels-photo-7937299.jpeg?auto=compress&cs=tinysrgb&w=1400",
    bullets: ["Rete idrica e sanitari", "Centrali termiche"],
  },
  {
    slug: "termici",
    code: "02",
    name: "Termici",
    tag: "Specializzati in radianti",
    icon: Flame,
    desc:
      "La nostra specializzazione: impianti termici con soluzioni radianti a pavimento, soffitto, parete e fresato. Comfort uniforme, bassi consumi, integrazione con caldaie a condensazione e pompe di calore.",
    img: "/images/impianto-radiante-pavimento.jpg",
    bullets: [
      "Radianti — pavimento, soffitto, parete, fresato",
    ],
    featured: true,
  },
  {
    slug: "vmc",
    code: "03",
    name: "Sistemi HVAC",
    tag: "Qualità dell'aria",
    icon: Wind,
    desc:
      "Ventilazione meccanica controllata con recupero di calore ad alta efficienza. Filtrazione, deumidificazione e integrazione con l'impianto radiante per un microclima costante tutto l'anno.",
    img: "https://images.pexels.com/photos/32032996/pexels-photo-32032996.jpeg?auto=compress&cs=tinysrgb&w=1400",
    bullets: ["Sistemi di ricircolo, deumidificazione e benessere dell'aria"],
  },
];

export function Impianti() {
  return (
    <section
      id="impianti"
      data-testid={TEST_IDS.impianti.section}
      className="relative py-28 md:py-44 border-t border-white/10 bg-[#0A0C10]"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 mb-16 md:mb-24 items-end">
          <div className="col-span-12 md:col-span-7">
            <FadeUp>
              <span className="eyebrow">
                <span className="inline-block mr-3 w-6 h-px bg-[#E31E24] align-middle" />
                Impianti · Cosa realizziamo
              </span>
            </FadeUp>
            <h2 className="mt-6 font-display font-black uppercase tracking-tighter leading-[0.92] text-4xl sm:text-5xl md:text-6xl lg:text-7xl">
              <MaskedLineReveal>Tre discipline,</MaskedLineReveal>
              <MaskedLineReveal delay={0.15}>
                <span className="font-editorial italic font-light">un solo</span>
              </MaskedLineReveal>
              <MaskedLineReveal delay={0.3}>cantiere.</MaskedLineReveal>
            </h2>
          </div>
          <div className="col-span-12 md:col-span-4 md:col-start-9 text-white/60 leading-relaxed">
            <FadeUp delay={0.2}>
              <p className="text-base md:text-lg max-w-md">
                Impianti idrosanitari, sistemi HVAC, impianti termici con
                specializzazione in sistemi radianti.
              </p>
            </FadeUp>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 auto-rows-[minmax(360px,1fr)] gap-px bg-white/10 border border-white/10">
          {PILLARS.map((p, i) => (
            <PillarCard key={p.slug} p={p} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function PillarCard({ p, i }) {
  const Icon = p.icon;
  const span = p.featured ? "md:col-span-6" : "md:col-span-3";
  return (
    <motion.article
      data-testid={TEST_IDS.impianti.card(p.slug)}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.8, delay: i * 0.08, ease: [0.76, 0, 0.24, 1] }}
      className={`group relative bg-[#0A0C10] p-8 md:p-10 flex flex-col justify-between overflow-hidden min-h-[420px] ${span}`}
    >
      {/* Image behind */}
      <div className="spotlight-frame absolute inset-0 opacity-30 group-hover:opacity-95 transition-opacity duration-700">
        <img
          src={p.img}
          alt={p.name}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0C10] via-[#0A0C10]/85 to-[#0A0C10]/60 group-hover:from-[#0A0C10] group-hover:via-[#0A0C10]/50 group-hover:to-transparent transition-all duration-700" />
      </div>

      {/* Top */}
      <div className="relative flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 border border-white/20 flex items-center justify-center text-[#E31E24] group-hover:border-[#E31E24] group-hover:bg-[#E31E24] group-hover:text-[#0A0C10] transition-colors duration-500">
            <Icon size={18} />
          </div>
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/50">
            / {p.code}
          </div>
        </div>
        <ArrowUpRight
          size={22}
          className="text-white/40 group-hover:text-[#E31E24] group-hover:-translate-y-1 group-hover:translate-x-1 transition-all duration-500"
        />
      </div>

      {/* Middle-bottom */}
      <div className="relative mt-auto">
        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#E31E24] mb-3">
          {p.tag}
        </div>
        <h3
          className={`font-display font-black uppercase tracking-tighter leading-none ${
            p.featured
              ? "text-4xl md:text-5xl lg:text-6xl"
              : "text-3xl md:text-4xl"
          }`}
        >
          {p.name}
        </h3>
        <p className="mt-4 text-white/70 text-sm md:text-base leading-relaxed max-w-md">
          {p.desc}
        </p>

        <ul className="mt-6 space-y-2 border-t border-white/10 pt-5">
          {p.bullets.map((b, k) => (
            <li
              key={k}
              className="flex items-start gap-3 text-white/55 text-sm font-mono"
            >
              <span className="text-[#E31E24] mt-2 inline-block w-2 h-px bg-[#E31E24]" />
              <span className="tracking-wide">{b}</span>
            </li>
          ))}
        </ul>
      </div>
    </motion.article>
  );
}
